import * as React from 'react';
import { useIntl } from 'react-intl';
import { JsonLd } from 'react-schemaorg';

import { Product as SchemaProduct, Review } from 'schema-dts';

import { GetReviewsQuery } from '@notino/shared/definitions/types';

import { Product } from '@containers/ProductDetailContainer/model';
import { useSsrProductUrl } from '@containers/ProductDetailContainer/ProductDetail/hooks/useSsrProductUrl';
import { useVariantOffers } from '@containers/ProductDetailContainer/ProductDetail/ProductJsonLd/useVariantOffers';
import {
  getAggregateRating,
  getImagesSrc,
} from '@containers/ProductDetailContainer/ProductDetail/ProductJsonLd/utils';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';

interface IReviewsJsonLdProps {
  product: Product;
  selectedVariant: Product['variants'][number];
  imageDomainPath: string;
  reviews: GetReviewsQuery;
}

export const ReviewsJsonLd = React.memo(
  ({
    product,
    selectedVariant,
    imageDomainPath,
    reviews: reviewsData,
  }: IReviewsJsonLdProps) => {
    const { url } = useSsrProductUrl(product);
    const { formatMessage } = useIntl();
    const { shopSettings } = useProductDetailContext();

    const countryCode = shopSettings?.countryCode || '';

    const reviewsList: Review[] =
      reviewsData?.reviewsAndFilters?.data?.reviews?.map((review) => ({
        '@type': 'Review' as const,
        reviewBody: review.text ?? '',
        reviewRating: {
          '@type': 'Rating' as const,
          ratingValue: review.score,
        },
        author: {
          '@type': 'Person' as const,
          name: review.userName,
        },
      })) ?? [];

    const { discountedVariantOffers, variantOffers } = useVariantOffers({
      formatMessage,
      imageDomainPath,
      itemCountry: countryCode,
      product: product as any,
    });

    return (
      <JsonLd<SchemaProduct>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          '@id': url ?? undefined,
          sku: product.masterProductCode,
          gtin13: selectedVariant.eanCode ?? undefined,
          category: product.annotation ?? undefined,
          name: `${product.brand?.name ?? ''} ${product.collection ?? ''}`,
          description: product.description ?? undefined,
          brand: {
            '@type': 'Brand',
            name: product.brand?.name ?? '',
          },
          image: getImagesSrc(selectedVariant, imageDomainPath),
          offers: [...discountedVariantOffers, ...variantOffers],
          review: reviewsList,
          ...(product.reviewOverview &&
            product.reviewOverview.count > 0 && {
              aggregateRating: getAggregateRating(product.reviewOverview),
            }),
        }}
      />
    );
  }
);
